import React, { useEffect, useState } from 'react';

import AddIcon from '@mui/icons-material/Add';
import LockIcon from '@mui/icons-material/Lock';
import {
    Box,
    Button,
    Card,
    Collapse,
    Menu,
    MenuItem,
    Typography,
} from '@sunwisesoftware/sunwise-ui';
import { isEmpty } from 'lodash';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { compose } from 'recompose';
import { createStructuredSelector } from 'reselect';

import EmptyState from 'common/components/EmptyState';
import {
    POLYGON_MAP,
    POLYGON_OBSTACLE,
    POLYGON_SEGMENT,
    POLYGON_CIRCLE,
} from 'common/components/segment/constants';
import { handleOpenChangeSegments } from 'common/components/segment/helpers';
import MenuOptions from 'common/components/segment/MenuOptions';
import SegmentErrors from 'common/components/segment/SegmentErrors';
import ShowErrors from 'common/components/ShowErrors';
import { numberFormat } from 'common/utils/helpers';
import { getCountryCurrencyLocale } from 'common/utils/helpers/session';

import EmptyLayout from 'resources/EmptyLayout.svg?react';

import * as selectors from '../selectors';

import FilterSegments from './FilterSegments';
import SegmentEditCard from './SegmentEditCard';

const filterSegments = (segments, selectedType) => {
    if (selectedType === 'all') return segments;
    return segments.filter(
        (segment) => segment.type === parseInt(selectedType),
    );
};

const SegmentsListCard = ({
    commercialOfferId,
    handleClickAdd,
    handleClickDelete,
    handleClickEditSegment,
    handleOnDuplicateSegment,
    handleSelectSegment,
    initialValues,
    isDisabledEdition,
    isDrawingMode,
    isEditionMode,
    mapRef,
    onCloseDrawer,
    segments,
    selectedSegmentId,
    toggleLockSegment,
    updateSegments,
}) => {
    const { t } = useTranslation();
    const isDisabled = isDrawingMode || isEditionMode;
    const [segmentsList, setSegmentsList] = useState([]);
    const [selectedType, setSelectedType] = useState('all');
    const [anchorEl, setAnchorEl] = React.useState(null);

    useEffect(() => {
        const filteredSegments = filterSegments(segments, selectedType);
        setSegmentsList(filteredSegments);
    }, [segments]);

    useEffect(() => {
        if (!isEmpty(initialValues)) {
            const filteredSegments = filterSegments(segments, 'all');
            setSegmentsList(filteredSegments);
            setSelectedType('all');
        }
    }, [initialValues]);

    if (segments?.length === 0) {
        return (
            <EmptyState
                customCaption={
                    <Typography fontWeight="bold" variant="body2">
                        {t("Looks like you don't have segments yet. Add one!")}
                    </Typography>
                }
                onClick={() => handleClickAdd({ type: POLYGON_SEGMENT })}
                sizeImage={160}
                srcSvg={<EmptyLayout />}
                sxCard={{ boxShadow: 'none !important' }}
                textButton={t('Add segment')}
            />
        );
    }

    const polygonContent = ({ item }) => {
        return (
            <>
                <Typography variant="body2">
                    {item?.panel?.name || 'N/A'}
                </Typography>
                <Typography variant="body2">
                    {t('Panel', {
                        count: parseInt(item?.quantity_enabled_modules),
                    })}
                    : {item.quantity_enabled_modules} u
                </Typography>
                <Typography variant="body2">{item?.system_name}</Typography>
            </>
        );
    };

    const obstacleContent = ({ item }) => (
        <>
            <Typography variant="body2">
                {t('Total area')}{' '}
                {numberFormat(item?.total_area, {
                    decimals: 2,
                    locale: getCountryCurrencyLocale(),
                    style: 'decimal',
                })}
                m<sup>2</sup>
            </Typography>
            {item?.drawing_type === POLYGON_CIRCLE && (
                <Typography variant="body2">
                    {t('Diameter')}: {item?.diameter}m
                </Typography>
            )}
            <Typography variant="body2">
                {t('Height')}: {item?.height}m
            </Typography>
        </>
    );

    const renderContent = ({ item }) => {
        if (item.type === POLYGON_SEGMENT) return polygonContent({ item });
        if (item.type === POLYGON_OBSTACLE) return obstacleContent({ item });
    };

    const open = Boolean(anchorEl);
    const handleClick = (event) => setAnchorEl(event.currentTarget);
    const handleClose = () => setAnchorEl(null);

    const handleOnClickAdd = (type) => {
        handleClickAdd({ type });
        setAnchorEl(null);
    };

    return (
        <>
            <Box sx={{ display: 'flex', gap: 2 }}>
                <Box sx={{ width: '100%' }}>
                    <Button
                        disabled={isDisabled || isDisabledEdition}
                        fullWidth
                        onClick={handleClick}
                        startIcon={<AddIcon />}
                        variant="outlined"
                    >
                        {t('Add')}
                    </Button>
                    <Menu
                        anchorEl={anchorEl}
                        anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'right',
                        }}
                        onClose={handleClose}
                        open={open}
                        transformOrigin={{
                            vertical: 'top',
                            horizontal: 'right',
                        }}
                        PaperProps={{
                            sx: { width: anchorEl?.clientWidth || 0 },
                        }}
                    >
                        <MenuItem
                            onClick={() => handleOnClickAdd(POLYGON_SEGMENT)}
                        >
                            {t('Segment')}
                        </MenuItem>
                        <MenuItem
                            onClick={() => handleOnClickAdd(POLYGON_OBSTACLE)}
                        >
                            {t('Obstacle')}
                        </MenuItem>
                        <MenuItem
                            onClick={() => handleOnClickAdd(POLYGON_CIRCLE)}
                        >
                            {t('Circular obstacle')}
                        </MenuItem>
                        <MenuItem onClick={() => handleOnClickAdd(POLYGON_MAP)}>
                            {t('Image')}
                        </MenuItem>
                    </Menu>
                </Box>

                <FilterSegments
                    segments={segments}
                    selectedType={selectedType}
                    setSegmentsList={setSegmentsList}
                    setSelectedType={setSelectedType}
                />
            </Box>

            {segmentsList.map((item) => {
                const errorsArrays = item?.segment_errors?.error?.arrays || [];
                const errorsSystem = item?.segment_errors?.error?.system || [];
                const warningsArrays =
                    item?.segment_errors?.warning?.arrays || [];
                const warningsSystem =
                    item?.segment_errors?.warning?.system || [];

                return (
                    <Card key={item.id} sx={{ mt: 2 }}>
                        <Card.Body>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <Box
                                    onClick={() => {
                                        if (!isDisabled) {
                                            handleSelectSegment(item, segments);
                                            handleOpenChangeSegments({
                                                item,
                                                segments,
                                                updateSegments,
                                            });
                                        }
                                    }}
                                    sx={{
                                        cursor: 'pointer',
                                        flexGrow: 1,
                                        width: '80%',
                                    }}
                                >
                                    <Typography
                                        fontWeight="bold"
                                        noWrap
                                        sx={{
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            fontSize: '14px',
                                        }}
                                        variant="body2"
                                    >
                                        {item.locked && (
                                            <LockIcon sx={{ fontSize: 14 }} />
                                        )}{' '}
                                        {item.name}
                                    </Typography>
                                </Box>

                                <Box sx={{ ml: 'auto' }}>
                                    <MenuOptions
                                        commercialOfferId={commercialOfferId}
                                        handleClickDelete={handleClickDelete}
                                        handleOnDuplicateSegment={
                                            handleOnDuplicateSegment
                                        }
                                        handleClickEditSegment={
                                            handleClickEditSegment
                                        }
                                        isDisabled={
                                            isDisabled || isDisabledEdition
                                        }
                                        isHidden={
                                            item.id !== selectedSegmentId ||
                                            !isEmpty(initialValues)
                                        }
                                        item={item}
                                        toggleLockSegment={toggleLockSegment}
                                    />
                                </Box>
                            </Box>

                            <SegmentErrors
                                baseId={item.id}
                                errors={errorsArrays}
                                severity="error"
                            />

                            <ShowErrors
                                errors={errorsSystem}
                                severity="error"
                            />

                            <SegmentErrors
                                baseId={item.id}
                                errors={warningsArrays}
                            />

                            <ShowErrors errors={warningsSystem} />

                            <Collapse
                                key={`collapse-${item.id}-${item.open}`}
                                in={
                                    item.open ||
                                    (!isEmpty(initialValues) &&
                                        item.id === initialValues.id)
                                }
                                unmountOnExit
                            >
                                <Box
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        cursor: 'pointer',
                                    }}
                                >
                                    {isEmpty(initialValues) &&
                                        renderContent({ item })}

                                    {!isEmpty(initialValues) &&
                                        item.id === initialValues.id && (
                                            <SegmentEditCard
                                                commercialOfferId={
                                                    commercialOfferId
                                                }
                                                mapRef={mapRef}
                                                onCloseDrawer={onCloseDrawer}
                                            />
                                        )}
                                </Box>
                            </Collapse>
                        </Card.Body>
                    </Card>
                );
            })}
        </>
    );
};

const mapStateToProps = createStructuredSelector({
    initialValues: selectors.getInitialValues,
});

SegmentsListCard.propTypes = {
    commercialOfferId: PropTypes.string,
    handleClickAdd: PropTypes.func,
    handleClickDelete: PropTypes.func,
    handleClickEditSegment: PropTypes.func,
    handleOnDuplicateSegment: PropTypes.func,
    handleSelectSegment: PropTypes.func,
    initialValues: PropTypes.object,
    isDisabledEdition: PropTypes.bool,
    isDrawingMode: PropTypes.bool,
    isEditionMode: PropTypes.bool,
    mapRef: PropTypes.object,
    onCloseDrawer: PropTypes.func,
    segments: PropTypes.array,
    selectedSegmentId: PropTypes.string,
    toggleLockSegment: PropTypes.func,
    updateSegments: PropTypes.func,
};

export default compose(connect(mapStateToProps, null))(SegmentsListCard);
